import { useState } from 'react';
import { Calculator, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ExtinguisherCalculator() {
    const [surface, setSurface] = useState<number>(0);
    const [activity, setActivity] = useState<'industrial' | 'tertiary'>('tertiary');
    const [height, setHeight] = useState<number>(3);
    const [steps, setSteps] = useState<string[]>([]);

    const calculateRequirements = () => {
        const calculationSteps: string[] = [];
        let baseDivisor = activity === 'tertiary' ? 200 : 150;

        calculationSteps.push(`1. Surface Area: ${surface}m²`);
        calculationSteps.push(`2. Activity Type: ${activity === 'tertiary' ? 'Tertiary/Office (1/200m²)' : 'Industrial (1/150m²)'}`);

        let rawCount = surface / baseDivisor;
        let count = Math.round(rawCount);
        if (count < 1) count = 1;

        calculationSteps.push(`3. Base Calculation: ${surface} / ${baseDivisor} = ${rawCount.toFixed(2)}`);
        calculationSteps.push(`4. Standard Rounding (APSAD R4): ${count} unit(s)`);

        return { count, steps: calculationSteps };
    };

    const result = calculateRequirements();

    return (
        <div className="card-terminal p-6 animate-fadeIn">
            <div className="flex items-center gap-3 mb-6 border-b border-[var(--blueprint-line)] pb-4">
                <div className="w-10 h-10 rounded-lg bg-ember-500/20 flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-ember-500" />
                </div>
                <div>
                    <h2 className="text-xl font-display font-bold text-white">Extinguisher Sizing</h2>
                    <p className="text-sm text-gray-400">APSAD R4 / Code du Travail</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Inputs */}
                <div className="space-y-6">
                    <div>
                        <label className="label-terminal">Surface Area (m²)</label>
                        <input
                            type="number"
                            value={surface}
                            onChange={(e) => setSurface(Math.max(0, Number(e.target.value)))}
                            className="input-terminal"
                            placeholder="e.g. 1000"
                        />
                    </div>

                    <div>
                        <label className="label-terminal">Activity Type</label>
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                onClick={() => setActivity('tertiary')}
                                className={`px-4 py-3 rounded-lg border transition-all ${activity === 'tertiary'
                                        ? 'bg-ember-500/20 border-ember-500 text-white'
                                        : 'bg-transparent border-[var(--blueprint-line)] text-gray-400 hover:bg-white/5'
                                    }`}
                            >
                                Tertiary / Office
                            </button>
                            <button
                                onClick={() => setActivity('industrial')}
                                className={`px-4 py-3 rounded-lg border transition-all ${activity === 'industrial'
                                        ? 'bg-ember-500/20 border-ember-500 text-white'
                                        : 'bg-transparent border-[var(--blueprint-line)] text-gray-400 hover:bg-white/5'
                                    }`}
                            >
                                Industrial
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="label-terminal">Ceiling Height (m)</label>
                        <input
                            type="number"
                            value={height}
                            onChange={(e) => setHeight(Math.max(0, Number(e.target.value)))}
                            className="input-terminal"
                            placeholder="e.g. 3"
                        />
                        {height > 3 && (
                            <div className="mt-2 text-xs text-yellow-500 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Height &gt; 3m may require complementary protection
                            </div>
                        )}
                    </div>
                </div>

                {/* Results */}
                <div className="bg-black/40 rounded-xl p-6 border border-[var(--blueprint-line)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-50">
                        <Calculator className="w-24 h-24 text-[var(--blueprint-line)]" />
                    </div>

                    <h3 className="text-lg font-semibold text-white mb-6 relative z-10">Requirements Analysis</h3>

                    <div className="space-y-4 relative z-10">
                        <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-[var(--blueprint-line-light)]">
                            <span className="text-gray-400">Base Units Required</span>
                            <span className="text-3xl font-bold text-ember-500">{result.count}</span>
                        </div>

                        <div className="space-y-2">
                            <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">Calculation Log</div>
                            {result.steps.map((step, idx) => (
                                <div key={idx} className="text-sm text-gray-400 font-mono pl-2 border-l border-[var(--blueprint-line)]">
                                    {step}
                                </div>
                            ))}
                        </div>

                        {/* Complementary Protection Logic */}
                        <div className="mt-6 pt-4 border-t border-[var(--blueprint-line)]">
                            <h4 className="text-sm font-semibold text-white mb-3">Recommended Configuration</h4>
                            <ul className="space-y-2">
                                <li className="flex items-start gap-2 text-sm text-gray-300">
                                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                                    <span>{result.count}x 6L Water Spray (Eau Pulvérisée)</span>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-gray-300">
                                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                                    <span>Placed max 15m apart</span>
                                </li>
                                {height > 3 && (
                                    <li className="flex items-start gap-2 text-sm text-yellow-400">
                                        <Info className="w-4 h-4 mt-0.5 shrink-0" />
                                        <span>Consider wheeled powder units for high storage</span>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
